import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import { QuestionImage } from '../shared';

const ResultItem = memo(({ result, formatElapsedTime, renderQuestionDetails, isExpanded, onToggle }) => {
  return (
    <div className="bg-pink-50 p-4 rounded-lg shadow">
      <div className="flex justify-between items-center mb-2">
        <span className="font-semibold text-purple-700">
          第{result.attemptNumber}回
        </span>
        <span className="text-sm text-gray-600">
          {new Date(result.timestamp).toLocaleString()}
        </span>
      </div>

      <div className="flex items-center gap-6 mb-4 text-purple-600">
        <div>
          正答率: {result.correctRate.toFixed(1)}%
        </div>
        <div>
          正解数: {result.correctCount}/{result.total}
        </div>
        <div>
          所要時間: {formatElapsedTime(result.elapsedTime)}
        </div>
      </div>

      <button
        onClick={onToggle}
        className="w-full text-left p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
      >
        {isExpanded ? '詳細を閉じる' : '詳細を表示'}
      </button>

      {isExpanded && result.detailedResults && (
        <div className="mt-4 space-y-4">
          {result.detailedResults.map((question, qIndex) => (
            <div key={qIndex} className="bg-white p-4 rounded shadow-sm">
              {renderQuestionDetails(question)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

const ResultsModal = memo(({ isOpen, onClose, results, contentTitle }) => {
  const [expandedResultId, setExpandedResultId] = useState(null);
  const modalRef = useRef(null);

  // モーダルが開いている間、bodyのスクロールを無効化する
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      // 結果モーダルが閉じられたり、アンマウントしたときに呼ばれるクリーンアップで auto に戻す
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const formatElapsedTime = useCallback((ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}分${remainingSeconds}秒`;
  }, []);

  const handleToggleDetail = useCallback((resultId) => {
    setExpandedResultId(expandedResultId === resultId ? null : resultId);
  }, [expandedResultId]);

  const renderQuestionDetails = useCallback((question) => {
    const details = question.details;
    const response = details.userResponse;

    // まずは「時間切れ」なのかをチェック
    let resultText = '';
    let resultClass = '';

    if (response.explanation === "時間切れです。") {
      // 時間切れ
      resultText = '× 時間切れ';
      resultClass = 'text-red-600';
    } else if (question.isCorrect) {
      // 正解
      resultText = '✓ 正解';
      resultClass = 'text-blue-600';
    } else {
      // 不正解
      resultText = '× 不正解';
      resultClass = 'text-red-600';
    }

    return (
      <div className="bg-white p-4 rounded-lg shadow mb-4">
        <div className="mb-2">
          <span className={`font-semibold ${resultClass}`}>
            {resultText}
          </span>
        </div>

        <div className="space-y-2">
          {/* 画像表示（存在する場合） */}
          {details.hasImage && details.questionImage && (
            <div className="mb-3">
              <QuestionImage 
                imageData={details.questionImage} 
                alt="問題の画像"
                className="max-w-sm"
              />
            </div>
          )}

          {/* 問題文表示 */}
          {details.type !== 'sentence-rearrangement' && details.question && (
            <p className="text-gray-800">
              <span className="font-medium">問題: </span>
              {question.question}
            </p>
          )}

          {/* 問題タイプ別の表示 */}
          {details.type === 'multiple-choice' && (
            <div className="ml-4">
              <p className="text-sm text-gray-600">選択肢:</p>
              <ul className="list-disc ml-4">
                {details.choices.map((choice, idx) => (
                  <li key={idx} className={`
                    ${choice === details.answer ? 'text-blue-600 font-semibold' : ''}
                    ${choice === response.userAnswer && choice !== details.answer ? 'text-green-600' : ''}
                  `}>
                    {choice}
                    {choice === details.answer && ' (正解)'}
                    {choice === response.userAnswer && choice !== details.answer && ' (あなたの回答)'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {details.type === 'sentence-rearrangement' && (
            <>
              <p className="text-gray-700 mb-2">
                <span className="font-medium">問題（日本語）: </span>
                {details.japaneseTranslation}
              </p>
              <p className="text-blue-600 mb-2">
                <span className="font-medium text-gray-700">正解: </span>
                {details.englishSentence}
              </p>
              <p className="text-green-600">
                <span className="font-medium text-gray-700">あなたの回答: </span>
                {response.explanation === "時間切れです。" ? 
                  "時間切れ" : 
                  (response?.userAnswer || '回答なし')
                }
              </p>
            </>
          )}

          {details.type === 'true-false' && (
            <>
              <p className="text-blue-600">
                <span className="font-medium text-gray-700">正解: </span>
                {details.answer ? '正しい' : '誤り'}
              </p>
              <p className="text-green-600">
                <span className="font-medium text-gray-700">回答: </span>
                {response.explanation === "時間切れです。" ? 
                  "時間切れ" : 
                  (response.userAnswer ? '正しい' : '誤り')
                }
              </p>
            </>
          )}

          {details.type === 'fill-in-the-blank' && (
            <>
              <p className="text-blue-600">
                <span className="font-medium text-gray-700">正解: </span>
                {details.answer}
              </p>
              <p className="text-green-600">
                <span className="font-medium text-gray-700">回答: </span>
                {response.explanation === "時間切れです。" ? 
                  "時間切れ" : 
                  response.userAnswer
                }
              </p>
            </>
          )}

          {details.type === 'matching' && (
            <>
              <p className="text-gray-700">
                <span className="font-medium">マッチング:</span>
              </p>
              <div className="ml-4">
                {details.leftItems.map((leftItem, idx) => (
                  <div key={idx} className="flex gap-4">
                    <span>{leftItem}</span>
                    <span>→</span>
                    <span>
                      {response.explanation === "時間切れです。" ? 
                        "時間切れ" : (
                          <>
                            <span className="text-green-600">
                              {details.rightItems[response.userAnswer?.[idx] || 0]}
                            </span>
                            {details.correctMatches[idx] !== response.userAnswer?.[idx] && 
                              <span className="text-blue-600">
                                {` (正解: ${details.rightItems[details.correctMatches[idx]]})`}
                              </span>
                            }
                          </>
                        )
                      }
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* 解説 */}
          {details.explanation && (
            <div className="mt-2 text-gray-600 bg-gray-50 p-2 rounded">
              <span className="font-medium">解説: </span>
              {details.explanation === "時間切れです。" ? 
                "時間切れのため、解答できませんでした。" : 
                details.explanation}
            </div>
          )}
        </div>
      </div>
    );
  }, []);

  if (!isOpen || !results) return null;

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-0 bg-black opacity-50" />
      <div 
        ref={modalRef}
        className="relative bg-yellow-50 rounded-lg shadow-lg max-w-4xl w-full m-4 flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* ヘッダー部分 - 固定位置 */}
        <div className="px-6 py-4 bg-yellow-50 rounded-t-lg border-b border-yellow-100">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-purple-800">
              {contentTitle} - 挑戦結果一覧
            </h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* スクロール可能なコンテンツ部分 - 別コンテナ */}
        <div className="flex-1 overflow-hidden rounded-b-lg">
          <div className="px-6 py-4 max-h-[calc(90vh-100px)] overflow-y-auto">
            <div className="space-y-6 pb-4">
              {!results || results.length === 0 ? (
                <p className="text-gray-600 text-center py-4">
                  まだ挑戦履歴はありません
                </p>
              ) : (
                results.map(result => (
                  <ResultItem
                    key={result.timestamp}
                    result={result}
                    formatElapsedTime={formatElapsedTime}
                    renderQuestionDetails={renderQuestionDetails}
                    isExpanded={expandedResultId === result.timestamp}
                    onToggle={() => handleToggleDetail(result.timestamp)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

ResultItem.propTypes = {
  result: PropTypes.object.isRequired,
  formatElapsedTime: PropTypes.func.isRequired,
  renderQuestionDetails: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired
};

ResultsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  results: PropTypes.array,
  contentTitle: PropTypes.string
};

ResultItem.displayName = 'ResultItem';
ResultsModal.displayName = 'ResultsModal';

export default ResultsModal; 